import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const MetronomeContext = createContext();

export const useMetronome = () => {
  const context = useContext(MetronomeContext);
  if (!context) {
    throw new Error('useMetronome must be used within a MetronomeProvider');
  }
  return context;
};

const RHYTHM_TYPES = {
  quarter: { 
    id: 'quarter', 
    name: 'Quarter Note', 
    subdivisions: 1, 
    componentLabels: ['1']
  },
  eighth: { 
    id: 'eighth', 
    name: 'Eighth Note', 
    subdivisions: 2, 
    componentLabels: ['1', '&']
  },
  triplet: { 
    id: 'triplet', 
    name: 'Triplet', 
    subdivisions: 3, 
    componentLabels: ['1', '2', '3']
  },
  sixteenth: { 
    id: 'sixteenth', 
    name: 'Sixteenth Note', 
    subdivisions: 4, 
    componentLabels: ['1', 'e', '&', 'a']
  },
  quintuplet: { 
    id: 'quintuplet', 
    name: 'Quintuplet', 
    subdivisions: 5, 
    componentLabels: ['1', '2', '3', '4', '5']
  },
  septuplet: { 
    id: 'septuplet', 
    name: 'Septuplet', 
    subdivisions: 7, 
    componentLabels: ['1', '2', '3', '4', '5', '6', '7']
  }
};

export const MetronomeProvider = ({ children }) => {
  const [tempo, setTempo] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [accentBeats, setAccentBeats] = useState([0]);
  const [subdivisionAccents, setSubdivisionAccents] = useState({});
  const [currentRhythmType, setCurrentRhythmType] = useState(RHYTHM_TYPES.quarter);
  const [soundType, setSoundType] = useState('woodblock');
  const [volumeSettings, setVolumeSettings] = useState({
    main: 1.0,
    subdivisions: Array(7).fill(0.7)
  });
  const [rhythmComponentVolumes, setRhythmComponentVolumes] = useState({});
  const [currentBeat, setCurrentBeat] = useState(0);
  const [currentSubdivision, setCurrentSubdivision] = useState(0);
  const [audioInitialized, setAudioInitialized] = useState(false);
  
  const audioContextRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const timerRef = useRef(null);
  const audioBuffersRef = useRef({});
  const tapTimesRef = useRef([]);
  const lastTapTimeRef = useRef(0);
  const totalSubdivisionsRef = useRef(0);
  const currentCountRef = useRef(0);
  // Add a new ref for warmup sounds
  const warmupSoundRef = useRef(null);
  const currentSettingsRef = useRef({
    tempo,
    beatsPerMeasure,
    accentBeats,
    subdivisionAccents,
    currentRhythmType,
    soundType,
    volumeSettings,
    rhythmComponentVolumes
  });

  // Keep ref updated with latest settings
  useEffect(() => {
    currentSettingsRef.current = {
      tempo,
      beatsPerMeasure,
      accentBeats,
      subdivisionAccents,
      currentRhythmType,
      soundType,
      volumeSettings,
      rhythmComponentVolumes
    };
    
    // Update total subdivisions when time signature or rhythm type changes
    totalSubdivisionsRef.current = beatsPerMeasure * currentRhythmType.subdivisions;
  }, [tempo, beatsPerMeasure, accentBeats, subdivisionAccents, currentRhythmType, soundType, volumeSettings, rhythmComponentVolumes]);

  // Initialize audio context and buffers
  const initAudio = useCallback(async () => {
    try {
      // Create a new audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create audio buffers for different sound types
      const createBuffer = (type, subtype) => {
        const sampleRate = audioContextRef.current.sampleRate;
        const duration = 0.1;
        const buffer = audioContextRef.current.createBuffer(1, duration * sampleRate, sampleRate);
        const data = buffer.getChannelData(0);
        
        if (type === 'woodblock') {
          const baseFreq = subtype === 'accent' ? 800 : (subtype === 'main' ? 600 : 500);
          const decay = subtype === 'subdivision' ? 30 : 20;
          
          for (let i = 0; i < buffer.length; i++) {
            const t = i / sampleRate;
            const envelope = Math.exp(-t * decay);
            data[i] = envelope * Math.sin(2 * Math.PI * baseFreq * t);
          }
        } else if (type === 'click') {
          const decay = subtype === 'subdivision' ? 40 : 30;
          
          for (let i = 0; i < buffer.length; i++) {
            const t = i / sampleRate;
            data[i] = Math.exp(-t * decay) * (Math.random() * 2 - 1);
          }
        } else if (type === 'drum') {
          const baseFreq = subtype === 'accent' ? 150 : (subtype === 'main' ? 120 : 220);
          const decay = subtype === 'subdivision' ? 30 : 20;
          
          for (let i = 0; i < buffer.length; i++) {
            const t = i / sampleRate;
            const envelope = Math.exp(-t * decay);
            const drum = Math.sin(2 * Math.PI * baseFreq * Math.exp(-t * 5) * t);
            const noise = Math.random() * 2 - 1;
            data[i] = envelope * (0.7 * drum + 0.3 * noise);
          }
        } else if (type === 'bell') {
          const baseFreq = subtype === 'accent' ? 1200 : (subtype === 'main' ? 1000 : 800);
          const decay = subtype === 'subdivision' ? 10 : 8;
          
          for (let i = 0; i < buffer.length; i++) {
            const t = i / sampleRate;
            const envelope = Math.exp(-t * decay);
            const f1 = baseFreq;
            const f2 = baseFreq * 1.5;
            const f3 = baseFreq * 2;
            data[i] = envelope * (
              0.6 * Math.sin(2 * Math.PI * f1 * t) +
              0.3 * Math.sin(2 * Math.PI * f2 * t) +
              0.1 * Math.sin(2 * Math.PI * f3 * t)
            );
          }
        }
        
        return buffer;
      };

      // Initialize all sound types
      const soundTypes = ['woodblock', 'click', 'drum', 'bell'];
      const subtypes = ['main', 'accent', 'subdivision'];
      
      audioBuffersRef.current = {};
      soundTypes.forEach(type => {
        audioBuffersRef.current[type] = {};
        subtypes.forEach(subtype => {
          audioBuffersRef.current[type][subtype] = createBuffer(type, subtype);
        });
      });

      // Pre-create a silent buffer for immediate playback (warmup)
      const silentBuffer = audioContextRef.current.createBuffer(1, 1, audioContextRef.current.sampleRate);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = silentBuffer;
      source.connect(audioContextRef.current.destination);
      source.start(0);
      
      // Pre-create warmup sound nodes (to avoid first-play delay)
      const warmupType = soundType || 'woodblock';
      if (audioBuffersRef.current[warmupType] && audioBuffersRef.current[warmupType].main) {
        const warmupSource = audioContextRef.current.createBufferSource();
        const warmupGain = audioContextRef.current.createGain();
        warmupSource.buffer = audioBuffersRef.current[warmupType].main;
        warmupGain.gain.value = 0; // Silent
        warmupSource.connect(warmupGain);
        warmupGain.connect(audioContextRef.current.destination);
        warmupSoundRef.current = {
          source: warmupSource,
          gain: warmupGain
        };
      }
      
      setAudioInitialized(true);
      console.log("Audio initialized successfully");
      return true;
    } catch (error) {
      console.error('Audio initialization failed:', error);
      return false;
    }
  }, [soundType]);

  // Calculate beat and subdivision from the total count
  const calculateBeatAndSubdivision = useCallback((count, totalSubdivisions, beatsPerMeasure, subdivisions) => {
    const normalizedCount = count % totalSubdivisions;
    const beatIndex = Math.floor(normalizedCount / subdivisions);
    const subdivisionIndex = normalizedCount % subdivisions;
    
    return { beatIndex, subdivisionIndex };
  }, []);

  // Play sound - use currentSettingsRef to always use the latest settings
  const playSound = useCallback((time, count, immediate = false) => {
    if (!audioContextRef.current) {
      console.warn("Cannot play sound: Audio context not initialized");
      return;
    }
    
    // Always use the latest settings from ref
    const settings = currentSettingsRef.current;
    const currentSoundType = settings.soundType;
    const totalSubdivisions = settings.beatsPerMeasure * settings.currentRhythmType.subdivisions;
    
    if (!audioBuffersRef.current[currentSoundType]) {
      console.warn(`Sound type ${currentSoundType} not available`);
      return;
    }

    // Calculate beat and subdivision from count
    const { beatIndex, subdivisionIndex } = calculateBeatAndSubdivision(
      count,
      totalSubdivisions,
      settings.beatsPerMeasure,
      settings.currentRhythmType.subdivisions
    );

    const isMainBeat = subdivisionIndex === 0;
    const isAccentBeat = isMainBeat && settings.accentBeats.includes(beatIndex);
    const isAccentSubdivision = !isMainBeat && settings.subdivisionAccents[`${beatIndex}-${subdivisionIndex}`] === true;

    let bufferType;
    let volumeMultiplier;

    // Check if main volume is 0 (muted) - if so, don't play any sounds
    if (settings.volumeSettings.main <= 0) {
      return;
    }

    if (isAccentBeat || isAccentSubdivision) {
      bufferType = 'accent';
      volumeMultiplier = settings.volumeSettings.main;
    } else if (isMainBeat) {
      bufferType = 'main';
      volumeMultiplier = settings.volumeSettings.main;
    } else {
      bufferType = 'subdivision';
      volumeMultiplier = settings.volumeSettings.subdivisions[subdivisionIndex - 1] || 0.7;
      // Multiply by main volume to act as a master volume
      volumeMultiplier *= settings.volumeSettings.main;
    }

    if (settings.currentRhythmType.subdivisions > 1) {
      const componentKey = `${settings.currentRhythmType.id}-${subdivisionIndex}`;
      if (settings.rhythmComponentVolumes[componentKey] !== undefined) {
        volumeMultiplier *= settings.rhythmComponentVolumes[componentKey];
      }
    }

    try {
      const source = audioContextRef.current.createBufferSource();
      const gainNode = audioContextRef.current.createGain();

      source.buffer = audioBuffersRef.current[currentSoundType][bufferType];
      gainNode.gain.value = volumeMultiplier;

      source.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      if (immediate) {
        // For immediate playback, use a small offset to avoid click sounds
        const immediateTime = audioContextRef.current.currentTime + 0.001;
        source.start(immediateTime);
      } else {
        source.start(time);
      }
    } catch (error) {
      console.error("Error playing sound:", error);
    }
  }, [calculateBeatAndSubdivision]);

  // Update beat indicators visually, regardless of sound
  const updateBeatIndicators = useCallback((time, count) => {
    const settings = currentSettingsRef.current;
    const totalSubdivisions = settings.beatsPerMeasure * settings.currentRhythmType.subdivisions;
    
    // Calculate beat and subdivision from count
    const { beatIndex, subdivisionIndex } = calculateBeatAndSubdivision(
      count,
      totalSubdivisions,
      settings.beatsPerMeasure,
      settings.currentRhythmType.subdivisions
    );
    
    // Update visual beat display with the correct beat and subdivision
    const visualDelay = Math.max(0, (time - (audioContextRef.current?.currentTime || 0)) * 1000);
    setTimeout(() => {
      setCurrentBeat(beatIndex);
      setCurrentSubdivision(subdivisionIndex);
    }, visualDelay);
  }, [calculateBeatAndSubdivision]);

  // Schedule beats - use currentSettingsRef for the latest settings
  const schedule = useCallback(() => {
    if (!audioContextRef.current) {
      console.warn("Cannot schedule: Audio context not initialized");
      return;
    }
    
    // Always use the latest settings from ref
    const settings = currentSettingsRef.current;
    
    const secondsPerBeat = 60.0 / settings.tempo;
    const secondsPerSubdivision = secondsPerBeat / settings.currentRhythmType.subdivisions;
    const currentTime = audioContextRef.current.currentTime;
    const scheduleAheadTime = 0.1;

    while (nextNoteTimeRef.current < currentTime + scheduleAheadTime) {
      const count = currentCountRef.current;
      
      // Always update visual beat indicators, regardless of sound
      updateBeatIndicators(nextNoteTimeRef.current, count);
      
      // Calculate beat and subdivision for debugging/logging
      const { beatIndex, subdivisionIndex } = calculateBeatAndSubdivision(
        count,
        totalSubdivisionsRef.current,
        settings.beatsPerMeasure,
        settings.currentRhythmType.subdivisions
      );
      
      const isMainBeat = subdivisionIndex === 0;
      
      // Play sound - it will handle muting internally based on volume settings
      playSound(nextNoteTimeRef.current, count);

      // Advance to next subdivision
      nextNoteTimeRef.current += secondsPerSubdivision;
      currentCountRef.current = (currentCountRef.current + 1) % totalSubdivisionsRef.current;
    }

    timerRef.current = setTimeout(schedule, 25);
  }, [playSound, calculateBeatAndSubdivision, updateBeatIndicators]);

  // Play first beat immediately
  const playFirstBeat = useCallback(() => {
    if (!audioContextRef.current) return;
    
    const settings = currentSettingsRef.current;
    
    // Always update visual indicators for first beat
    updateBeatIndicators(audioContextRef.current.currentTime, 0);
    
    // Play sound - it will handle muting internally based on volume settings
    playSound(audioContextRef.current.currentTime, 0, true);
    
    // Set the next note time to be after this immediate beat
    const secondsPerBeat = 60.0 / settings.tempo;
    const secondsPerSubdivision = secondsPerBeat / settings.currentRhythmType.subdivisions;
    nextNoteTimeRef.current = audioContextRef.current.currentTime + secondsPerSubdivision;
    currentCountRef.current = 1; // Start from the second subdivision
  }, [playSound, updateBeatIndicators]);

  // Pre-warm the audio system
  const preWarmAudio = useCallback(() => {
    if (!audioContextRef.current || !warmupSoundRef.current) return;
    
    try {
      // Stop any existing warmup sound
      if (warmupSoundRef.current.source) {
        try {
          warmupSoundRef.current.source.stop();
        } catch (e) {
          // Ignore errors if sound was already stopped
        }
      }
      
      // Create a new warmup sound (silent)
      const warmupSource = audioContextRef.current.createBufferSource();
      const warmupGain = audioContextRef.current.createGain();
      
      const currentSoundType = currentSettingsRef.current.soundType;
      warmupSource.buffer = audioBuffersRef.current[currentSoundType].main;
      warmupGain.gain.value = 0; // Silent
      
      warmupSource.connect(warmupGain);
      warmupGain.connect(audioContextRef.current.destination);
      
      // Start and immediately stop to warm up the audio pipeline
      warmupSource.start(audioContextRef.current.currentTime);
      warmupSource.stop(audioContextRef.current.currentTime + 0.001);
      
      warmupSoundRef.current = {
        source: warmupSource,
        gain: warmupGain
      };
    } catch (error) {
      console.warn("Error pre-warming audio:", error);
    }
  }, []);

  // Toggle play/pause
  const togglePlay = useCallback(async () => {
    if (isPlaying) {
      setIsPlaying(false);
      clearTimeout(timerRef.current);
      setCurrentBeat(0);
      setCurrentSubdivision(0);
      currentCountRef.current = 0;
    } else {
      try {
        // Initialize audio if not already done
        if (!audioInitialized) {
          await initAudio();
        }

        // Resume audio context if suspended
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
          console.log("Audio context resumed");
        }

        // Pre-warm the audio system to reduce first-play latency
        preWarmAudio();

        // Start playback
        setIsPlaying(true);
        setCurrentBeat(0);
        setCurrentSubdivision(0);
        currentCountRef.current = 0;
        totalSubdivisionsRef.current = beatsPerMeasure * currentRhythmType.subdivisions;
        
        // Play the first beat immediately using a more direct approach
        setTimeout(() => {
          playFirstBeat();
          
          // Schedule subsequent beats with a slight delay to ensure first beat plays
          setTimeout(() => {
            schedule();
          }, 10);
        }, 0);
      } catch (error) {
        console.error("Error starting playback:", error);
        setIsPlaying(false);
      }
    }
  }, [isPlaying, audioInitialized, initAudio, schedule, beatsPerMeasure, currentRhythmType, playFirstBeat, preWarmAudio]);

  // Tap tempo
  const handleTapTempo = useCallback(() => {
    const now = performance.now();
    
    if (lastTapTimeRef.current > 0 && (now - lastTapTimeRef.current) > 2000) {
      tapTimesRef.current = [];
    }

    if (lastTapTimeRef.current > 0) {
      tapTimesRef.current.push(now - lastTapTimeRef.current);
    }

    if (tapTimesRef.current.length > 8) {
      tapTimesRef.current.shift();
    }

    if (tapTimesRef.current.length >= 2) {
      const avgInterval = tapTimesRef.current.reduce((sum, time) => sum + time, 0) / tapTimesRef.current.length;
      const newTempo = Math.round(60000 / avgInterval);
      setTempo(Math.max(30, Math.min(240, newTempo)));
    }

    lastTapTimeRef.current = now;
  }, []);

  // Update time signature - should take effect immediately
  const updateTimeSignature = useCallback((beats, accents = [0]) => {
    const newBeats = parseInt(beats);
    const newAccents = typeof accents === 'string' ? 
      accents.split(',').map(num => parseInt(num) - 1) : 
      Array.isArray(accents) ? accents : [accents - 1];
    
    setBeatsPerMeasure(newBeats);
    setAccentBeats(newAccents);
    
    // Reset subdivision accents when time signature changes
    setSubdivisionAccents({});
    
    // Update total subdivisions
    const newTotalSubdivisions = newBeats * currentRhythmType.subdivisions;
    totalSubdivisionsRef.current = newTotalSubdivisions;
    
    if (isPlaying) {
      // Reset count to beginning of measure but preserve playback
      currentCountRef.current = 0;
      setCurrentSubdivision(0);
      setCurrentBeat(0);
    }
  }, [isPlaying, currentRhythmType.subdivisions]);

  // Toggle accent - FIXED: Allow removing all accents including the first beat
  const toggleAccent = useCallback((beatIndex) => {
    setAccentBeats(prev => {
      const isCurrentlyAccent = prev.includes(beatIndex);
      
      if (isCurrentlyAccent) {
        // Always allow removal of accent, even if it's the only one
        return prev.filter(index => index !== beatIndex);
      } else {
        // Add accent
        return [...prev, beatIndex].sort((a, b) => a - b);
      }
    });
  }, []);

  // Toggle subdivision accent - new function
  const toggleSubdivisionAccent = useCallback((beatIndex, subdivisionIndex) => {
    // Don't allow toggling accent on the first subdivision (0)
    // as that's controlled by the main beat accent
    if (subdivisionIndex === 0) {
      toggleAccent(beatIndex);
      return;
    }
    
    setSubdivisionAccents(prev => {
      const key = `${beatIndex}-${subdivisionIndex}`;
      const newAccents = { ...prev };
      
      if (newAccents[key]) {
        delete newAccents[key];
      } else {
        newAccents[key] = true;
      }
      
      return newAccents;
    });
  }, [toggleAccent]);

  // Update rhythm type - should take effect immediately
  const updateRhythmType = useCallback((typeId) => {
    const rhythmType = RHYTHM_TYPES[typeId];
    if (rhythmType) {
      setCurrentRhythmType(rhythmType);
      
      // Reset subdivision accents when rhythm type changes
      setSubdivisionAccents({});
      
      // Update total subdivisions
      const newTotalSubdivisions = beatsPerMeasure * rhythmType.subdivisions;
      totalSubdivisionsRef.current = newTotalSubdivisions;
      
      if (isPlaying) {
        // Reset count to beginning of measure but preserve playback
        currentCountRef.current = 0;
        setCurrentSubdivision(0);
      }
    }
  }, [isPlaying, beatsPerMeasure]);

  // Initialize audio on mount
  useEffect(() => {
    // We'll initialize audio on first user interaction instead of on mount
    // This helps with autoplay policy restrictions in browsers
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Add a document-level click handler to initialize audio on first interaction
  useEffect(() => {
    const initAudioOnInteraction = async () => {
      if (!audioInitialized) {
        await initAudio();
        
        // Pre-warm the audio system right after initialization
        setTimeout(preWarmAudio, 100);
      }
      document.removeEventListener('click', initAudioOnInteraction);
    };

    document.addEventListener('click', initAudioOnInteraction);
    
    return () => {
      document.removeEventListener('click', initAudioOnInteraction);
    };
  }, [audioInitialized, initAudio, preWarmAudio]);

  const value = {
    // State
    tempo,
    isPlaying,
    beatsPerMeasure,
    accentBeats,
    subdivisionAccents,
    currentRhythmType,
    soundType,
    volumeSettings,
    rhythmComponentVolumes,
    currentBeat,
    currentSubdivision,
    RHYTHM_TYPES,
    audioInitialized,
    
    // Actions
    setTempo,
    togglePlay,
    handleTapTempo,
    updateTimeSignature,
    toggleAccent,
    toggleSubdivisionAccent,
    updateRhythmType,
    setSoundType,
    setVolumeSettings,
    setRhythmComponentVolumes
  };

  return (
    <MetronomeContext.Provider value={value}>
      {children}
    </MetronomeContext.Provider>
  );
};